/**
 * UploadTextSeries.jsx — Feb 9 2026.
 *
 * Writer flow for creating a Text Series + adding episodes.
 *
 * Two-step flow:
 *   1. Pick existing series (if writer has any) OR create new
 *   2. Add an episode to the picked/new series
 *
 * Readers consume these via /text-series/{id}/episodes/{episode_id}
 * (TextSeriesReader.jsx), which supports plain reading, flash + chunk
 * reading, and TTS playback (default voice free; HD consumes voice
 * minutes).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, CheckCircle2, ChevronRight, CircleAlert, FileText, ImagePlus, Loader2, PenLine, Plus } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import EpisodeStatsChip from '../components/EpisodeStatsChip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UploadTextSeries = () => {
  const navigate = useNavigate();
  const [mySeries, setMySeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesError, setSeriesError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('pick'); // 'pick' | 'new'

  // New series form
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [creating, setCreating] = useState(false);

  // Episode form (shows after a series is picked or created)
  const [epNumber, setEpNumber] = useState(1);
  const [epTitle, setEpTitle] = useState('');
  const [epBody, setEpBody] = useState('');
  const [savingEpisode, setSavingEpisode] = useState(false);
  const [episodeError, setEpisodeError] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const episodeWordCount = useMemo(() => (epBody.trim() ? epBody.trim().split(/\s+/).filter(Boolean).length : 0), [epBody]);

  const refreshSeries = async () => {
    setSeriesLoading(true);
    setSeriesError('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/text-series?mine=true`);
      if (!r.ok) { setSeriesError('We could not load your series. Please try again.'); return; }
      const d = await r.json();
      setMySeries(d.series || []);
    } catch {
      setSeriesError('We could not reach your writing library. Check your connection and try again.');
    } finally {
      setSeriesLoading(false);
    }
  };
  useEffect(() => { refreshSeries(); }, []);

  const onCoverChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Pick an image file'); return; }
    setCoverFile(f);
    const fr = new FileReader();
    fr.onload = () => setCoverPreview(fr.result);
    fr.readAsDataURL(f);
  };

  const createSeries = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('author', author.trim());
      fd.append('description', description.trim());
      fd.append('genre', genre.trim());
      if (coverFile) fd.append('cover_image', coverFile);
      const r = await authFetch(`${BACKEND_URL}/api/text-series`, { method: 'POST', body: fd });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not create series');
        return;
      }
      const d = await r.json();
      toast.success('Series created — add your first episode');
      setMySeries((cur) => [d.series, ...cur]);
      setSelectedId(d.series.series_id);
      setMode('pick');
      setEpNumber(1);
      setEpTitle('Episode 1');
      // Clear new-series form
      setTitle(''); setAuthor(''); setDescription(''); setGenre('');
      setCoverFile(null); setCoverPreview(null);
    } finally {
      setCreating(false);
    }
  };

  const selectedSeries = mySeries.find((s) => s.series_id === selectedId);
  // Gap-aware state — when an earlier episode was deleted, the new
  // /next-episode-number endpoint reports `has_gap: true` + a
  // `gap_number`. The form then locks the number, surfaces a banner,
  // and the backend will reject any higher episode_number anyway.
  const [gapInfo, setGapInfo] = useState({ has_gap: false, gap_number: null, taken: [] });

  useEffect(() => {
    let alive = true;
    if (!selectedSeries) return;
    const fallback = (selectedSeries.episode_count || 0) + 1;
    setEpNumber(fallback);
    setEpTitle('');
    setGapInfo({ has_gap: false, gap_number: null, taken: [] });
    (async () => {
      try {
        const r = await authFetch(
          `${BACKEND_URL}/api/text-series/${selectedSeries.series_id}/next-episode-number`,
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        if (d.next_episode_number) setEpNumber(d.next_episode_number);
        setGapInfo({
          has_gap: !!d.has_gap,
          gap_number: d.gap_number,
          taken: d.taken || [],
        });
      } catch { /* fallback already set */ }
    })();
    return () => { alive = false; };
  }, [selectedId, selectedSeries]);

  const saveEpisode = async () => {
    setEpisodeError('');
    if (!selectedId) { setEpisodeError('Choose a series before saving an episode.'); return; }
    if (!epBody.trim()) { setEpisodeError('Add episode text before saving.'); return; }
    // Iter 79: enforce 1000-5000 word window client-side before we
    // even hit the network. Matches the backend guard so the writer
    // gets an instant, specific error.
    const wc = episodeWordCount;
    if (wc < 1000) {
      setEpisodeError(`Episode too short — minimum 1,000 words (yours: ${wc.toLocaleString()}).`);
      return;
    }
    if (wc > 5000) {
      setEpisodeError(`Episode too long — maximum 5,000 words (yours: ${wc.toLocaleString()}). Split it into multiple episodes.`);
      return;
    }
    setSavingEpisode(true);
    try {
      const fd = new FormData();
      // episode_number is auto-computed server-side (sentinel = 0).
      // Title is the scribe's subtitle only — backend prefixes
      // "Episode N: " automatically.
      fd.append('episode_number', '0');
      fd.append('title', epTitle.trim());
      fd.append('body_text', epBody.trim());
      const r = await authFetch(`${BACKEND_URL}/api/text-series/${selectedId}/episodes`, { method: 'POST', body: fd });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        // 409 gap errors return a structured detail dict; prefer the
        // human-readable `message` so the toast doesn't show [object Object].
        const detail = e.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.message || 'Could not save episode');
        setEpisodeError(msg);
        return;
      }
      const d = await r.json();
      // Feb 12 2026 user bug — Book Series episodes weren't surfacing
      // the scribe's XP gain. Backend now returns xp_awarded +
      // coins_awarded; we show both in the toast so the scribe
      // immediately sees their reward.
      const xpBit = d.xp_awarded ? ` · +${d.xp_awarded} XP` : '';
      const coinBit = d.coins_awarded ? ` · +${d.coins_awarded} coins` : '';
      toast.success(
        `Episode ${d.episode.episode_number} saved (${d.episode.word_count} words)${xpBit}${coinBit}`,
      );
      setLastSaved(d.episode);
      // Bump the local episode_count + reset form
      setMySeries((cur) => cur.map((s) => s.series_id === selectedId
        ? { ...s, episode_count: (s.episode_count || 0) + 1 } : s));
      // Re-fetch the next slot — could be a higher gap if more episodes
      // are still missing, or jump from N to N+1 in a contiguous series.
      try {
        const rr = await authFetch(
          `${BACKEND_URL}/api/text-series/${selectedId}/next-episode-number`,
        );
        if (rr.ok) {
          const dd = await rr.json();
          setEpNumber(dd.next_episode_number || (epNumber + 1));
          setGapInfo({
            has_gap: !!dd.has_gap,
            gap_number: dd.gap_number,
            taken: dd.taken || [],
          });
        } else {
          setEpNumber((n) => n + 1);
        }
      } catch {
        setEpNumber((n) => n + 1);
      }
      setEpTitle('');
      setEpBody('');
      setEpisodeError('');
    } finally {
      setSavingEpisode(false);
    }
  };

  return (
    <main className="sharp-bg min-h-screen pb-32" data-testid="upload-text-series-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
      <button
        type="button"
        onClick={() => navigate('/upload-hub')}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-5"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Upload Hub
      </button>

      <header className="mb-6 rounded-3xl border border-white/10 bg-gradient-to-br from-[#17142b] via-[#12131A] to-[#12131A] p-5 sm:p-7 shadow-2xl shadow-black/20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="eyebrow text-indigo-300">Scribe workspace</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight leading-[1.02] mt-1">Text Series<span className="text-indigo-300">.</span></h1>
            <p className="max-w-2xl text-sm leading-relaxed text-white/60 mt-3">Create a serial, choose the next available episode, and publish a complete 1,000–5,000 word chapter. Readers can read, flash-read, chunk-read, or listen via TTS.</p>
          </div>
          <div className="shrink-0 rounded-2xl border border-indigo-400/25 bg-indigo-400/10 p-3" aria-label="Publishing requirements">
            <PenLine className="w-5 h-5 text-indigo-200 mb-2" aria-hidden="true" />
            <p className="text-[10px] uppercase tracking-[0.16em] font-black text-indigo-200">Episode window</p>
            <p className="text-sm font-bold tabular-nums text-white mt-0.5">1,000–5,000 words</p>
          </div>
        </div>
      </header>

      {/* Workspace switch */}
      <div className="grid grid-cols-2 gap-2 mb-5 rounded-2xl border border-white/10 bg-[#12131A] p-2" role="tablist" aria-label="Text series workspace">
        <button
          type="button"
          onClick={() => setMode('pick')}
          role="tab"
          aria-selected={mode === 'pick'}
          className={`min-h-12 rounded-xl border px-3 py-2.5 transition ${mode === 'pick' ? 'border-indigo-400 bg-indigo-400/10' : 'border-white/10 hover:border-white/30 bg-white/[0.02]'}`}
          data-testid="upload-text-series-pick-tab"
        >
          <p className={`text-xs font-black uppercase tracking-widest ${mode === 'pick' ? 'text-indigo-300' : 'text-white/55'}`}>Add to existing</p>
        </button>
        <button
          type="button"
          onClick={() => { setMode('new'); setSelectedId(null); }}
          role="tab"
          aria-selected={mode === 'new'}
          className={`min-h-12 rounded-xl border px-3 py-2.5 transition ${mode === 'new' ? 'border-amber-400 bg-amber-400/10' : 'border-white/10 hover:border-white/30 bg-white/[0.02]'}`}
          data-testid="upload-text-series-new-tab"
        >
          <p className={`text-xs font-black uppercase tracking-widest ${mode === 'new' ? 'text-amber-300' : 'text-white/55'}`}>Create new</p>
        </button>
      </div>

      {/* PICK MODE */}
      {mode === 'pick' && (
        <section className="mb-6" data-testid="upload-text-series-pick">
          <div className="flex items-end justify-between gap-3 mb-3"><div><p className="eyebrow text-indigo-300">Your library</p><h2 className="font-display text-2xl font-black text-white">Choose a series</h2></div>{mySeries.length > 0 && <span className="text-xs tabular-nums text-white/45">{mySeries.length} saved</span>}</div>
          {seriesLoading ? (
            <div className="rounded-2xl border border-white/10 bg-[#12131A] p-5 text-sm text-white/55 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-indigo-300" /> Loading your library…</div>
          ) : seriesError ? (
            <div className="rounded-2xl border border-rose-400/25 bg-rose-500/10 p-4 text-sm text-rose-100" role="alert"><div className="flex gap-2"><CircleAlert className="w-4 h-4 shrink-0 mt-0.5" /> <span>{seriesError}</span></div><button type="button" onClick={refreshSeries} className="mt-3 text-xs font-black uppercase tracking-widest text-rose-100 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-200/70 rounded">Try again</button></div>
          ) : mySeries.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 bg-[#12131A] p-7 text-center"><BookOpen className="w-7 h-7 text-indigo-300 mx-auto mb-3" aria-hidden="true" /><p className="font-display font-black text-white">Your serial library is ready.</p><p className="text-sm text-white/50 mt-1">Create your first series, then return here to add episodes.</p><button type="button" className="mt-4 min-h-10 rounded-xl border border-amber-400/30 px-3 text-xs font-black uppercase tracking-widest text-amber-200 hover:bg-amber-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70" onClick={() => setMode('new')}>Create new</button></div>
          ) : (
            <div className="space-y-2">
              {mySeries.map((s) => (
                <button
                  key={s.series_id}
                  type="button"
                  onClick={() => setSelectedId(s.series_id)}
                  className={`w-full min-h-16 text-left rounded-2xl border px-4 py-3 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300/70 ${selectedId === s.series_id ? 'border-indigo-400 bg-indigo-400/10' : 'border-white/10 hover:border-white/30 bg-[#12131A]'}`}
                  data-testid={`upload-text-series-pick-${s.series_id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-indigo-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-black text-white truncate">{s.title}</p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-0.5 tabular-nums">
                        {s.episode_count || 0} {s.episode_count === 1 ? 'episode' : 'episodes'} · {s.genre || 'general'}
                      </p>
                    </div>
                    {selectedId === s.series_id && <CheckCircle2 className="w-4 h-4 text-indigo-300" />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* NEW MODE */}
      {mode === 'new' && (
        <section className="mb-6 space-y-3" data-testid="upload-text-series-new">
          <label className="block">
            <p className="eyebrow text-white/40 mb-1">Series title</p>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="e.g. The Lagos Chronicles"
              className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50"
              data-testid="upload-text-series-title"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label>
              <p className="eyebrow text-white/40 mb-1">Author</p>
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                maxLength={120}
                placeholder="Pen name"
                className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50"
                data-testid="upload-text-series-author"
              />
            </label>
            <label>
              <p className="eyebrow text-white/40 mb-1">Genre</p>
              <input
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                maxLength={60}
                placeholder="fantasy / romance / sci-fi"
                className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50"
                data-testid="upload-text-series-genre"
              />
            </label>
          </div>
          <label className="block">
            <p className="eyebrow text-white/40 mb-1">Description</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="What's it about?"
              className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50 resize-y"
              data-testid="upload-text-series-description"
            />
          </label>
          <label className="block">
            <p className="eyebrow text-white/40 mb-1">Cover image (optional)</p>
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5"><ImagePlus className="w-4 h-4 text-indigo-300 shrink-0" aria-hidden="true" /><input type="file" accept="image/*" onChange={onCoverChange} className="min-w-0 text-white text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-400/15 file:px-2.5 file:py-1.5 file:text-xs file:font-bold file:text-indigo-100 hover:file:bg-indigo-400/25" data-testid="upload-text-series-cover" /></div>
            <p className="text-[10px] text-white/40 mt-1">Upload an image for the series cover. AI cover generation is not offered by this text-series endpoint.</p>
            {coverPreview && <img src={coverPreview} alt="" className="mt-2 rounded-lg w-32 h-32 object-cover border border-white/10" />}
          </label>
          <button
            type="button"
            onClick={createSeries}
            disabled={creating}
            className="bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black py-3 px-6 rounded-full uppercase tracking-widest text-xs inline-flex items-center gap-2"
            data-testid="upload-text-series-create"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create series
          </button>
        </section>
      )}

      {/* EPISODE FORM — shown when a series is selected */}
      {selectedSeries && (
        <section
          className="rounded-2xl border border-indigo-400/30 bg-[#12131A] p-4 sm:p-5"
          data-testid="upload-text-series-episode-form"
        >
          <p className="eyebrow text-indigo-300">Add episode to</p>
          <p className="font-display text-2xl font-black text-white tracking-tight mt-0.5">{selectedSeries.title}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1 tabular-nums">
            {selectedSeries.episode_count || 0} {selectedSeries.episode_count === 1 ? 'episode' : 'episodes'} so far
          </p>

          {/* Auto-numbered episode N · subtitle row — iter 67.
              The "#" field is now a read-only chip showing the next
              episode in the series (server-truth). The writer just
              types a subtitle (or leaves blank to get just
              "Episode N"). Backend handles prefixing. */}
          <div className="grid grid-cols-3 gap-2 mt-4 items-end">
            <div className="col-span-1">
              <p className="eyebrow text-white/40 mb-1">Episode #</p>
              <div
                className={`w-full rounded-xl text-white text-sm px-3 py-2.5 inline-flex items-center justify-between ${gapInfo.has_gap ? 'bg-amber-500/10 border border-amber-400/40' : 'bg-[#0B0C10] border border-indigo-400/30'}`}
                data-testid="upload-text-episode-number"
              >
                <span className="font-display font-black text-base tabular-nums">{epNumber}</span>
                <span className={`text-[9px] uppercase tracking-[0.25em] font-extrabold ${gapInfo.has_gap ? 'text-amber-300' : 'text-indigo-300'}`}>
                  {gapInfo.has_gap ? 'Fill gap' : 'Auto'}
                </span>
              </div>
            </div>
            <label className="col-span-2">
              <p className="eyebrow text-white/40 mb-1">Subtitle <span className="text-white/30 font-normal">(optional)</span></p>
              <input
                value={epTitle}
                onChange={(e) => setEpTitle(e.target.value)}
                placeholder={`Episode ${epNumber}${epTitle ? '' : ' …'}`}
                maxLength={200}
                className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-indigo-400/50"
                data-testid="upload-text-episode-title"
              />
            </label>
          </div>
          {gapInfo.has_gap && (
            <div
              className="mt-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100"
              data-testid="upload-text-gap-banner"
            >
              <span className="font-bold uppercase tracking-widest text-amber-300 mr-1.5">Gap detected</span>
              Episode {gapInfo.gap_number} is missing in this series. You must refill it before
              you can add later episodes.
            </div>
          )}
          <p className="text-[10px] text-white/40 mt-1.5" data-testid="upload-text-episode-preview">
            Saves as <span className="text-indigo-300 font-bold">
              {epTitle.trim() ? `Episode ${epNumber}: ${epTitle.trim()}` : `Episode ${epNumber}`}
            </span>
          </p>
          <label className="block mt-3">
            <p className="eyebrow text-white/40 mb-1">Episode text</p>
            <textarea
              value={epBody}
              onChange={(e) => { setEpBody(e.target.value); if (episodeError) setEpisodeError(''); }}
              placeholder="Type or paste the full episode here…"
              rows={12}
              className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-indigo-400/50 resize-y"
              data-testid="upload-text-episode-body"
            />
            {/* Live word count + readability preview — same formulas
                readers see. Turns red when out of the 1000-5000 word
                publish window so scribes catch it before hitting save.
                Iter 68 (stats chip) + Iter 79 (word guard). */}
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {(() => {
                const wc = episodeWordCount;
                const tooShort = wc > 0 && wc < 1000;
                const tooLong = wc > 5000;
                const ok = wc >= 1000 && wc <= 5000;
                const color = ok
                  ? 'text-emerald-300 border-emerald-400/40 bg-emerald-400/10'
                  : (tooShort || tooLong)
                    ? 'text-red-300 border-red-400/50 bg-red-500/10'
                    : 'text-white/50 border-white/10 bg-white/5';
                let msg = `min 1,000 · max 5,000`;
                if (tooShort) msg = `need ${(1000 - wc).toLocaleString()} more`;
                else if (tooLong) msg = `over by ${(wc - 5000).toLocaleString()}`;
                else if (ok) msg = 'ready to publish';
                return (
                  <span
                    className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-black px-2.5 py-1 rounded-full border ${color}`}
                    data-testid="upload-text-episode-word-count"
                  >
                    <span className="tabular-nums">{wc.toLocaleString()} words</span>
                    <span className="opacity-70">· {msg}</span>
                  </span>
                );
              })()}
              <EpisodeStatsChip text={epBody} />
            </div>
          </label>
          {episodeError && <p className="mt-3 rounded-xl border border-rose-400/25 bg-rose-500/10 px-3 py-2 text-xs text-rose-100" role="alert">{episodeError}</p>}
          <button
            type="button"
            onClick={saveEpisode}
            disabled={savingEpisode}
            className="mt-3 w-full sm:w-auto bg-indigo-400 hover:bg-indigo-300 disabled:opacity-60 text-black font-black py-3 px-6 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
            data-testid="upload-text-episode-save"
          >
            {savingEpisode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {savingEpisode ? 'Saving episode…' : 'Save episode'}
          </button>
          {lastSaved && (
            <p className="text-[11px] text-emerald-300 mt-3" data-testid="upload-text-episode-confirmation">
              ✓ Episode {lastSaved.episode_number}: {lastSaved.title} saved ({lastSaved.word_count} words).
              <button
                onClick={() => navigate(`/text-series/${selectedId}/episode/${lastSaved.episode_id}`)}
                className="ml-2 underline inline-flex items-center gap-0.5"
              >
                Open <ChevronRight className="w-3 h-3" />
              </button>
            </p>
          )}
        </section>
      )}
      </div>
    </main>
  );
};

export default UploadTextSeries;
